import Layout from "components/Layout";
import LegalContentItem from "components/LegalContentItem";
import LegalHeader from "components/LegalHeader";
import Seo from "components/seo";
import {
    AnnotationQuestionIcon,
    BuildingIcon,
    DataFlowIcon,
    FileDownloadIcon,
    GlobeIcon,
    HourglassIcon,
    MessageAlertCircleIcon,
    UserRightIcon,
} from "components/whatsInsideSidebarPrivacy/icons";
import { WhatsInsideSidebarPrivacy } from "components/whatsInsideSidebarPrivacy/whatsInsideSidebarPrivacy";
import type { PageProps } from "gatsby";
import { type FC, useEffect, useRef, useState } from "react";
import * as styles from "./styles.module.scss";

const HowToComplain = () => (
    <div className={styles.howToComplainWrapper}>
        <p className={styles.title}>How to Complain</p>

        <p className={styles.text}>
            If you have any concerns about our use of your personal information, you can make a complaint to us using
            the contact details in the "contact information" section of this notice.
        </p>
        <p className={styles.text}>
            If you remain unhappy with how we’ve used your data after raising a complaint with us, you can also complain
            to the{" "}
            <a href="https://autoriteitpersoonsgegevens.nl/en/submitting-a-tip-off-or-a-complaint-to-the-dutch-dpa">
                Dutch Data Protection Authority
            </a>
            , as we are based in the Netherlands.
        </p>
        <p className={styles.text}>
            You also choose to file a complaint at your national data protection authority. If you are a resident of a
            member state of the EEA, you can consult the list of data protection authorities in the EEA{" "}
            <a href="https://www.edpb.europa.eu/about-edpb/about-edpb/members_en">here</a>.
        </p>
        <p className={styles.notice}>This notice was last updated on 31 October 2024</p>
    </div>
);

export const cardsInfo = [
    {
        title: "What personal information we collect, use, and why",
        icon: FileDownloadIcon,
        id: "what-personal-info-we-collect",
    },
    {
        title: "Lawful bases and data protection rights",
        icon: BuildingIcon,
        id: "lawful-bases-and-data-protection-right",
    },
    {
        title: "Where we get personal information from",
        icon: DataFlowIcon,
        id: "where-we-get-personal-information",
    },
    {
        title: "How long we keep personal information",
        icon: HourglassIcon,
        id: "how-long-we-keep-personal-information",
    },
    {
        title: "Who we share personal information with",
        icon: UserRightIcon,
        id: "who-we-share-personal-information-with",
    },
    {
        title: "Transfers outside the European Economic Area (EEA)",
        icon: GlobeIcon,
        id: "transfers-outside-the-eea",
    },
    {
        title: "Get in touch with us",
        icon: MessageAlertCircleIcon,
        id: "get-in-touch-with-us",
    },
    {
        title: "How to complain",
        icon: AnnotationQuestionIcon,
        id: "how-to-complain",
    },
];

const scrollToElementWithOffset = (element: HTMLElement | null, offset = -40) => {
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY + offset;
    window.scrollTo({ top, behavior: "smooth" });
};

const Privacy = () => {
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const OFFSET_TOP_PX = -140;

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
                    .forEach(entry => {
                        setActiveSection(entry.target.id);
                    });
            },
            {
                // This makes the observed element "intersect" when it's 40px *below* top
                rootMargin: `${OFFSET_TOP_PX}px 0px -80% 0px`,
                threshold: 0,
            }
        );

        cardsInfo.forEach(item => {
            const el = sectionRefs.current[item.id];
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const handleClick = (id: string) => {
        const el = sectionRefs.current[id];
        scrollToElementWithOffset(el, OFFSET_TOP_PX);
    };

    return (
        <Layout bg="dark">
            <LegalHeader
                title="Privacy Policy"
                subHeading='This privacy notice tells you what to expect us, Assertive Yield B.V. (hereafter: "Assertive Yield", "we", or "us"), to do with your personal information; which is any information which directly or indirectly could be used to identify you. This notice relates to information shared directly with Assertive Yield, either through our website or by signing up to our services. For any data we process on behalf of our customers, their respective Privacy Policy regulates how that data is used.'
            />
            <div className={styles.wrapper}>
                <div className="container">
                    <div className={styles.contentWrapper}>
                        <div className={styles.sidebarWrapper}>
                            <WhatsInsideSidebarPrivacy handleClick={handleClick} activeSection={activeSection} />
                        </div>
                        <div className={styles.content}>
                            <LegalContentItem
                                title="What Personal Information We Collect, Use, and Why"
                                ref={el => (sectionRefs.current["what-personal-info-we-collect"] = el)}
                                id="what-personal-info-we-collect"
                            >
                                <p>
                                    We collect or use the following information to provide products and services to
                                    customers:
                                </p>
                                <ul>
                                    <li>Names and contact details;</li>
                                    <li>Purchase or account history;</li>
                                    <li>
                                        Payment details (including card or bank information for transfers and direct
                                        debits);
                                    </li>
                                    <li>Account information;</li>
                                    <li>Website user information (including user journeys and cookie tracking);</li>
                                    <li>Call recordings;</li>
                                    <li>Records of meetings and decisions;</li>
                                    <li>Information relating to compliments or complaints;</li>
                                    <li>Information used for security purposes;</li>
                                    <li>Marketing preferences;</li>
                                    <li>Correspondence.</li>
                                </ul>
                                <p>
                                    We collect or use the following information for service updates or marketing
                                    purposes:
                                </p>
                                <ul>
                                    <li>Names and contact details;</li>
                                    <li>Marketing preferences;</li>
                                    <li>Purchase or viewing history;</li>
                                    <li>IP addresses;</li>
                                    <li>Website user journey information;</li>
                                    <li>Records of consent, where appropriate.</li>
                                </ul>
                                <p>We collect or use the following information to comply with legal requirements:</p>
                                <ul>
                                    <li>Name and contact details;</li>
                                    <li>Financial transaction information.</li>
                                </ul>
                                <p>
                                    Assertive Yield does not utilize automated decision-making technologies producing
                                    legal or similarly significant effects.
                                </p>
                            </LegalContentItem>

                            <LegalContentItem
                                title="Lawful Bases and Data Protection Rights"
                                ref={el => (sectionRefs.current["lawful-bases-and-data-protection-right"] = el)}
                                id="lawful-bases-and-data-protection-right"
                            >
                                <p>
                                    Under the General Data Protection Regulation (GDPR), we must have a “lawful basis”
                                    for collecting and using your personal information. These are essentially legal
                                    justifications for processing your personal information.
                                </p>
                                <p>
                                    Which lawful basis we rely on affects your data protection rights, which are in
                                    brief set out below:
                                </p>
                                <ul>
                                    <li>
                                        <b>Your right of access</b> - You have the right to ask us for copies of your
                                        personal information. You can request other information such as details about
                                        where we get personal information from and who we share personal information
                                        with. There are some exemptions which means you do not receive all the
                                        information you ask for.
                                    </li>
                                    <li>
                                        <b>Your right to rectification</b> - You have the right to ask us to correct or
                                        delete personal information you think is inaccurate or incomplete.
                                    </li>
                                    <li>
                                        <b>Your right to erasure</b> - You have the right to ask us to delete your
                                        personal information.
                                    </li>
                                    <li>
                                        <b>Your right to restriction of processing</b> - You have the right to ask us to
                                        limit how we can use your personal information.
                                    </li>
                                    <li>
                                        <b>Your right to object to processing</b> - You have the right to object to the
                                        processing of your personal information.
                                    </li>
                                    <li>
                                        <b>Your right to data portability</b> - You have the right to ask that we
                                        transfer the personal information you gave us to another organisation, or to
                                        you.
                                    </li>
                                    <li>
                                        <b>Your right to withdraw consent</b> – When we use consent as our lawful basis
                                        you have the right to withdraw your consent at any time.
                                    </li>
                                </ul>
                                <p>
                                    Note that these rights are not enforceable in every case, even as outlined in the
                                    following section. If you make a request, we must respond to you without undue delay
                                    and in any event within one month. To make a data protection rights request, please
                                    contact us using the contact details in the "contact information" section of this
                                    notice.
                                </p>

                                <p className={styles.subHeading}>
                                    Our lawful bases for collecting or using personal information{" "}
                                    <b>to provide products and services to customers</b>
                                </p>

                                <ul>
                                    <li>
                                        <b>Contract</b> – we have to collect or use the information so we can enter into
                                        or carry out a contract with you. All of your data protection rights apply
                                        during the contract term except the right to erasure, the right to object, and
                                        the right to restrict data required to fulfill the contract.
                                    </li>
                                    <li>
                                        <b>Legal obligation</b> – we have to collect or use your information so we can
                                        comply with the law. All of your data protection rights apply, except the right
                                        to erasure, the right to object, the right to restrict (unless you contest the
                                        accuracy of your personal data or require us to continue to store it when it
                                        would otherwise be deleted), and the right to data portability.
                                    </li>
                                    <li>
                                        <b>Legitimate interests</b> – we’re collecting or using your information because
                                        it benefits you, our organisation or someone else, without causing an undue risk
                                        of harm to anyone. All of your data protection rights apply, except the right to
                                        portability. Our legitimate interests are:
                                    </li>
                                </ul>

                                <blockquote>
                                    We rely on the legitimate interest basis to perform analytics or otherwise gather
                                    insights on how customers interact with our products and services in order to
                                    improve our products and services. Where such analytics are gathered via cookies or
                                    other trackers, consent is relied on as a legal basis instead.
                                </blockquote>

                                <p className={styles.subHeading}>
                                    Our lawful bases for collecting or using personal information for service updates or
                                    marketing purposes are:
                                </p>

                                <p>
                                    Consent - we have permission from you after we gave you all the relevant
                                    information. All of your data protection rights apply.
                                </p>

                                <p className={styles.subHeading}>
                                    Our lawful bases for collecting or using personal information to comply with legal
                                    requirements
                                </p>

                                <ul>
                                    <li>
                                        <b>Contract</b> – we have to collect or use the information so we can enter into
                                        or carry out a contract with you. All of your data protection rights apply
                                        during the contract term except the right to erasure, the right to object, and
                                        the right to restrict data required to fulfill the contract.
                                    </li>
                                    <li>
                                        <b>Legal obligation</b> – we have to collect or use your information so we can
                                        comply with the law. All of your data protection rights apply, except the right
                                        to erasure, the right to object, the right to restrict (unless you contest the
                                        accuracy of your personal data or require us to continue to store it when it
                                        would otherwise be deleted), and the right to data portability.
                                    </li>
                                </ul>
                            </LegalContentItem>

                            <LegalContentItem
                                title="Where We Get Personal Information From"
                                ref={el => (sectionRefs.current["where-we-get-personal-information"] = el)}
                                id="where-we-get-personal-information"
                            >
                                <p>We get personal information from:</p>
                                <ul>
                                    <li>You directly;</li>
                                    <li>Publicly available sources.</li>
                                </ul>
                            </LegalContentItem>

                            <LegalContentItem
                                title="How Long We Keep Personal Information"
                                ref={el => (sectionRefs.current["how-long-we-keep-personal-information"] = el)}
                                id="how-long-we-keep-personal-information"
                            >
                                <p>
                                    We will only retain your personal data for as long as necessary to fulfil the
                                    purposes we collected it for, including for the purposes of satisfying any legal,
                                    accounting, or reporting requirements. If a person makes a legitimate request to
                                    erase their personal data, we will also delete their personal data in this case.
                                </p>
                                <p>
                                    If you want to learn more about our specific retention periods for your personal
                                    data established in our retention policy please contact us using the contact
                                    information provided in the "contact information" section of this notice.
                                </p>
                                <p>
                                    Upon expiry of the applicable retention period we will securely destroy your
                                    personal data in accordance with applicable laws and regulations.
                                </p>
                            </LegalContentItem>

                            <LegalContentItem
                                title="Who We Share Personal Information With"
                                ref={el => (sectionRefs.current["who-we-share-personal-information-with"] = el)}
                                id="who-we-share-personal-information-with"
                            >
                                <p>
                                    We do not share personal information with third parties, except as necessary for our
                                    legitimate professional and business needs, to carry out your requests, and/or as
                                    required or permitted by law or professional standards.
                                </p>
                                <p>The third parties with which we share personal information with include:</p>
                                <ul>
                                    <li>
                                        <b>Our service providers</b>: we transfer your personal information to our third
                                        party service providers, such as our (IT) systems providers, our hosting
                                        providers, our payroll providers, consultants (such as legal advisers) and other
                                        goods and services providers. Assertive Yield works with such providers so they
                                        can process your personal information on our behalf. Assertive Yield will only
                                        transfer personal information to them when they meet our strict standards on the
                                        processing of data and security. We only share personal information that allows
                                        them to provide their services.
                                    </li>
                                    <li>
                                        <b>Courts, tribunals, law enforcement or regulatory bodies</b>: Assertive Yield
                                        will disclose personal information in order to respond to requests of courts,
                                        tribunals, government or law enforcement agencies or where it is necessary or
                                        prudent to comply with applicable laws, court or tribunal orders or rules, or
                                        government or professional regulations.
                                    </li>
                                    <li>
                                        <b>Audits</b>: disclosures of personal information may also be needed for data
                                        privacy or security audits and/or to investigate or respond to a complaint or
                                        security threat.
                                    </li>
                                </ul>
                            </LegalContentItem>

                            <LegalContentItem
                                title="Transfers Outside the European Economic Area (EEA)"
                                ref={el => (sectionRefs.current["transfers-outside-the-eea"] = el)}
                                id="transfers-outside-the-eea"
                            >
                                <p>
                                    Where necessary, we transfer personal information outside of the EEA. When doing so,
                                    we ensure that we comply with the GDPR and other applicable data protection laws,
                                    making sure appropriate safeguards are in place. Where an adequacy decision or the
                                    EU-US Data Privacy Framework do not apply, we rely on the European Commission's
                                    Standard Contractual Clauses to secure international transfers.
                                </p>
                                <p>
                                    For further information, please contact us using the contact information provided in
                                    the "contact information" section of this notice.
                                </p>
                            </LegalContentItem>

                            <LegalContentItem
                                title="Get in Touch with Us"
                                ref={el => (sectionRefs.current["get-in-touch-with-us"] = el)}
                                id="get-in-touch-with-us"
                            >
                                <p>
                                    If you have any questions relating to this notice or would like to exercise your
                                    data subject rights, please reach out to{" "}
                                    <a href="mailto:hello@assertiveyield.com">hello@assertiveyield.com.</a>
                                </p>
                            </LegalContentItem>

                            <div ref={el => (sectionRefs.current["how-to-complain"] = el)} id="how-to-complain">
                                <HowToComplain />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
export const Head: FC<PageProps> = ({ location }) => (
    <Seo noIndex title="Privacy" titleSuffixed canonical={location.pathname}>
        <script type="application/ld+json">
            {`
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "${location.pathname}",
          "url": "${location.pathname}",
          "name": "Privacy",
          "datePublished": "2023-01-16T23:37:53+00:00",
          "dateModified": "2023-03-21T16:10:45+00:00",
          "description": "Assertive Yield is a global company, created by publishers to publishers. Pioneer in offering Yield Revenue Management in Real-Time, from all revenue sources, with extraordinary granular data, as never seen before.",
          "inLanguage": "en-US",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.assertiveyield.com/#website",
            "url": "https://www.assertiveyield.com/",
            "name": "Assertive Yield",
            "description": "Investment Management Solutions",
            "inLanguage": "en-US",
          "publisher": {
            "@type": "Organization",
            "@id": "https://www.assertiveyield.com/#organization",
            "name": "Assertive Yield",
            "url": "https://www.assertiveyield.com/",
            "sameAs": [
              "https://www.youtube.com/@assertiveyield",
              "https://www.linkedin.com/company/assertive-yield/"
            ],
            "logo": {
              "@type": "ImageObject",
              "@id": "https://www.assertiveyield.com/#/schema/logo/image/",
              "inLanguage": "en-US",
              "url": "https://assertiveyield.com/img/og-image.png",
              "contentUrl": "https://assertiveyield.com/img/og-image.png",
              "width": 479,
              "height": 189,
              "caption": "Assertive Yield"
            },
            "image": {
              "@type": "ImageObject",
              "@id": "https://www.assertiveyield.com/#/schema/logo/image/",
              "inLanguage": "en-US",
              "URL": "https://assertiveyield.com/img/og-image.png",
              "contentUrl": "https://assertiveyield.com/img/og-image.png",
              "width": 479,
              "height": 189,
              "caption": "Assertive Yield"
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://www.assertiveyield.com/?s={search_term_string}"
              },
              "query-input": {
                "@type": "PropertyValueSpecification",
                "valueRequired": "http://schema.org/True",
                "valueName": "search_term_string"
              }
            }
          }
        },
        "about": {
          "@type": "Organization",
          "@id": "https://www.assertiveyield.com/#organization",
          "name": "Assertive Yield",
          "url": "https://www.assertiveyield.com/",
          "sameAs": [
            "https://www.youtube.com/@assertiveyield",
            "https://www.linkedin.com/company/assertive-yield/"
          ],
          "logo": {
            "@type": "ImageObject",
            "@id": "https://www.assertiveyield.com/#/schema/logo/image/",
            "inLanguage": "en-US",
            "url": "https://assertiveyield.com/img/og-image.png",
            "contentUrl": "https://assertiveyield.com/img/og-image.png",
            "width": 479,
            "height": 189,
            "caption": "Assertive Yield"
          }
      },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "@id": "https://www.assertiveyield.com/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Assertive Yield",
              "item": {
                "@id": "https://www.assertiveyield.com/",
                "@type": "Thing"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Privacy"
            }
          ]
        },
        "potentialAction": {
          "@type": "ReadAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://www.assertiveyield.com${location.pathname}"
          }
        }
      }
      `}
        </script>
    </Seo>
);

export default Privacy;
